import type { ImportBankScheduleRow } from "../api";

function parseEuroMoney(raw: string): number | null {
  const cleaned = raw
    .replace(/EUR|€/gi, "")
    .trim()
    .replace(/\./g, "")
    .replace(",", ".");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parseEuroDate(raw: string): string | null {
  const m = raw.trim().match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return `${y}-${mo!.padStart(2, "0")}-${d!.padStart(2, "0")}`;
}

export type ParseBankScheduleResult = {
  rows: ImportBankScheduleRow[];
  skipped: number;
};

/**
 * Parsea una tabla pegada del banco con columnas:
 *   FECHA CUOTA | IMPORTE CUOTA | AMORTIZACIÓN | INTERESES | IMPORTE PENDIENTE DE AMORTIZACIÓN
 * Acepta separación por tabulador (pegado desde una web/hoja de cálculo) o
 * por 2+ espacios. Ignora líneas de cabecera y vacías (cualquier línea sin
 * fecha reconocible se cuenta como "skipped" en vez de fallar entera).
 */
export function parseBankSchedule(text: string): ParseBankScheduleResult {
  const rows: ImportBankScheduleRow[] = [];
  let skipped = 0;
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const cols = (trimmed.includes("\t") ? trimmed.split("\t") : trimmed.split(/\s{2,}/))
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    if (cols.length < 5) {
      skipped++;
      continue;
    }
    const date = parseEuroDate(cols[0]!);
    const total = parseEuroMoney(cols[1]!);
    const amortized = parseEuroMoney(cols[2]!);
    const interest = parseEuroMoney(cols[3]!);
    const pendingCapital = parseEuroMoney(cols[4]!);
    if (date === null || total === null || amortized === null || interest === null || pendingCapital === null) {
      skipped++;
      continue;
    }
    rows.push({ date, total, amortized, interest, pendingCapital });
  }
  rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return { rows, skipped };
}
